import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Banknote, FileText, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { ChequeDetails } from '@/components/postgrid/cheque-details/ChequeDetails';
import { LetterDetails } from '@/components/postgrid/postgrid-letters/letter-details/LetterDetails';
import { Skeleton } from '@/components/ui/skeleton';
import { enhancedApi } from '@/services/api/investigationRecordsRequestsService';

interface InFlightRequestsTableProps {
  matterId: string;
}

const InFlightRequestsTableSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-lg">
    <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
      <div className="flex items-center gap-2">
        <EnvelopeIcon className="w-4 h-4 text-gray-500" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
    <div className="divide-y divide-gray-100">
      {Array.from({ length: 2 }).map((_, idx) => (
        <div key={idx} className="px-4 py-2.5 flex items-center gap-3">
          <Skeleton className="w-4 h-4 rounded" />
          <div className="flex items-center gap-2 flex-1">
            <Skeleton className="h-4 w-32" />
            <span className="text-gray-300">·</span>
            <Skeleton className="h-4 w-12" />
            <span className="text-gray-300">·</span>
            <Skeleton className="h-4 w-28" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  </div>
);

export const InFlightRequestsTable = ({
  matterId,
}: InFlightRequestsTableProps) => {
  const { data: requests, isLoading } =
    enhancedApi.useInvestigationRecordsRequestsListQuery({ matterId });

  const [selectedLetterId, setSelectedLetterId] = useState<string | null>(null);
  const [selectedChequeId, setSelectedChequeId] = useState<string | null>(null);

  if (isLoading) {
    return <InFlightRequestsTableSkeleton />;
  }

  if (!requests || requests.length === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-700">
              Requests in Flight
            </span>
            <span className="text-xs text-gray-500">({requests.length})</span>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {requests.map((request) => {
            // Use actual request_type instead of inferring
            const isCheque = request.request_type === 'crash_report';
            const Icon = isCheque ? Banknote : FileText;

            let type = 'Request';
            if (request.request_type === 'crash_report') {
              type = 'Police Report Request';
            } else if (request.request_type === 'foia') {
              type = 'FOIA Request';
            }
            const deptName = request.police_department?.name || 'Unknown';
            const stateCode = request.police_department?.state_code;
            const sentDate = new Date(request.created_at).toLocaleDateString(
              'en-US',
              {
                month: 'short',
                day: 'numeric',
              },
            );

            // Check if postgrid object is linked yet
            const isLinked = isCheque
              ? !!request.postgrid_cheque_id
              : !!request.postgrid_letter_id;

            // Get status from postgrid
            const mailStatus = isCheque
              ? request.postgrid_cheque?.status
              : request.postgrid_letter?.status;
            const isInTransit =
              mailStatus &&
              [
                'in_transit',
                'in_local_area',
                'processed_for_delivery',
              ].includes(mailStatus);
            const isDelivered = mailStatus === 'delivered';
            const isDeposited =
              isCheque && request.postgrid_cheque?.date_cleared != null;
            const depositedDate =
              isDeposited && request.postgrid_cheque?.date_cleared
                ? new Date(
                    request.postgrid_cheque.date_cleared,
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })
                : null;

            const hasDetails = isLinked;

            const handleClick = () => {
              if (isCheque && request.postgrid_cheque_id) {
                setSelectedChequeId(request.postgrid_cheque_id);
              } else if (request.postgrid_letter_id) {
                setSelectedLetterId(request.postgrid_letter_id);
              }
            };

            return (
              <button
                key={request.id}
                type="button"
                onClick={hasDetails ? handleClick : undefined}
                disabled={!hasDetails}
                className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left disabled:cursor-default"
              >
                <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-sm text-gray-900 truncate">
                    {deptName}
                  </span>
                  {stateCode && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {stateCode}
                      </span>
                    </>
                  )}
                  <span className="text-gray-300">·</span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {type}
                  </span>
                  {!isLinked && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-amber-600 flex items-center gap-1 flex-shrink-0">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Preparing
                      </span>
                    </>
                  )}
                  {isLinked && isInTransit && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-blue-600 flex items-center gap-1 flex-shrink-0">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        In transit
                      </span>
                    </>
                  )}
                  {isLinked && isDelivered && !isDeposited && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-green-600 flex-shrink-0">
                        Delivered
                      </span>
                    </>
                  )}
                  {isDeposited && depositedDate && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-indigo-600 flex-shrink-0">
                        Cashed {depositedDate}
                      </span>
                    </>
                  )}
                </div>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {sentDate}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Inspector modals */}
      {selectedLetterId && (
        <LetterDetails
          letterId={selectedLetterId}
          open={!!selectedLetterId}
          onOpenChange={(open) => !open && setSelectedLetterId(null)}
        />
      )}
      {selectedChequeId && (
        <ChequeDetails
          chequeId={selectedChequeId}
          open={!!selectedChequeId}
          onOpenChange={(open) => !open && setSelectedChequeId(null)}
        />
      )}
    </>
  );
};
